import useReveal from "@/hooks/useReveal";

const PDF_BUILDING_PAINT =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/a1e31960-75d3-44b1-917f-9b32afd0b25a_-202026.03.pdf";
const PDF_BUILDING_ARCH =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/ad0de61e-4da2-4cf3-91ec-99765d504263_-202026.03.pdf";
const PDF_OVERSEAS_PAINT =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/2c38e554-2cbe-486a-9fb8-edde5e981be3_-.pdf";
const PDF_OVERSEAS_ARCH =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/fa2bc35b-41ea-484a-9b17-2b5bed997ce4_-.pdf";
const PDF_DISABILITY =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/25eea70b-82dd-4d8a-8c78-5017f29ab0a0_-2026.620.pdf";
const PDF_BUSINESS =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/eb3aace0-6c94-4ab7-a1cf-14d2c3edbb10_202026.03.20.pdf";
const PDF_SME =
  "https://storage.readdy-site.link/project_files/ac2cc551-d4dd-4bc1-9142-393457b4e539/ae228c0b-4ac9-4c4c-91ac-de6298d8bd97_-2026.04-2027.03.pdf";

const certs = [
  {
    icon: "ri-medal-2-line",
    title: "전문건설업 등록",
    sub: "도장공사업 · 습식방수공사업 · 석공사업 · 실내건축공사업",
    label: "건설산업기본법",
    links: [
      { name: "도장·방수·석공사업", url: PDF_BUILDING_PAINT },
      { name: "실내건축공사업", url: PDF_BUILDING_ARCH },
      { name: "해외 도장공사업", url: PDF_OVERSEAS_PAINT },
      { name: "해외 실내건축공사업", url: PDF_OVERSEAS_ARCH },
    ],
  },
  {
    icon: "ri-service-line",
    title: "장애인기업 인증",
    sub: "장애인기업 인증 · 공공기관 우선 협력사 자격 보유",
    label: "지방중소벤처기업청",
    links: [{ name: "장애인기업확인서", url: PDF_DISABILITY }],
  },
  {
    icon: "ri-file-shield-2-line",
    title: "사업자등록증",
    sub: "부산지방국세청 · 전문건설 법인 사업자",
    label: "국세청",
    links: [{ name: "사업자등록증", url: PDF_BUSINESS }],
  },
  {
    icon: "ri-shield-check-line",
    title: "각종 인증서",
    sub: "",
    label: "지방중소벤처기업청",
    links: [{ name: "중소기업확인서", url: PDF_SME }],
  },
];

export default function Certifications() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <section className="relative w-full py-14 md:py-24 lg:py-32 bg-navy-900 overflow-hidden">
      <div className="w-full px-6 md:px-12 lg:px-16">
        <div ref={ref} className="reveal max-w-3xl mb-10 md:mb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-point-400"></span>
            <span className="text-point-400 text-xs font-semibold uppercase tracking-[0.3em]">
              Licenses &amp; Certifications
            </span>
          </div>
          <h2 className="font-hero text-white text-3xl md:text-5xl lg:text-6xl leading-tight mb-4 md:mb-6">
            보유면허 &amp; 인증
          </h2>
          <p className="text-white/70 text-sm md:text-lg leading-relaxed">
            공공기관 발주·기업 협력사 등록에 필요한 모든 면허와 인증을 갖추고 있습니다.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {certs.map((c) => (
            <div
              key={c.title}
              className="group relative p-5 md:p-8 bg-white/[0.03] border border-white/10 rounded-2xl hover:bg-white/[0.06] hover:border-point-400 transition-all duration-500 ease-out hover:-translate-y-2"
            >
              <div className="w-11 h-11 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-point-500/15 text-point-400 mb-4 md:mb-6 group-hover:bg-point-500 group-hover:text-white transition-colors">
                <i className={`${c.icon} text-xl md:text-2xl`}></i>
              </div>
              <div className="text-xs text-point-400 uppercase tracking-widest mb-2 md:mb-3">
                {c.label}
              </div>
              <h3 className="font-hero text-white text-lg md:text-xl mb-3 md:mb-4">{c.title}</h3>
              <p className="text-xs md:text-sm text-white/60 leading-relaxed">{c.sub}</p>
              <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/10 flex flex-col gap-2.5">
                {c.links.map((l) => (
                  <a
                    key={l.name}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center justify-between gap-2 py-1 group/link cursor-pointer"
                  >
                    <span className="text-xs text-white/50 group-hover/link:text-white transition-colors leading-snug">
                      {l.name}
                    </span>
                    <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-white/5 text-white/70 group-hover/link:bg-point-500 group-hover/link:text-white transition-colors shrink-0">
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}